<template>
    <div class="overflow-hidden pt-24 lg:pt-32">
        <a id="main-content" class="fixed top-0" tabindex="-1"></a>

        <div class="container">
            <div class="grid grid-cols-12 base-responsive-gap">
                <div class="col-span-12 lg:col-span-8 lg:col-start-2">
                    <h1 class="header-1"><T t="our_first_10_years.title" /></h1>

                    <div class="lead mt-4 lg:mt-8">
                        <T t="our_first_10_years.description" />
                    </div>
                </div>
            </div>
        </div>
        <section class="side-scroll flex items-center justify-center mt-12 lg:mt-32 relative z-1">
            <div class="side-scroll-container w-full">
                <div class="side-scroll-list-wrapper" :class="{ 'relative w-full h-screen': $device.isDesktop }">
                    <div class="side-scroll-list" :class="{ 'absolute flex top-0 left-0': $device.isDesktop }">
                        <div
                            v-for="(item, i) in items"
                            :key="`slide-${i}`"
                            class="relative w-screen"
                            :class="{ 'h-screen': $device.isDesktop }"
                        >
                            <div>
                                <div class="container relative z-4 lg:mt-32 pb-44 lg:pb-0">
                                    <div class="grid grid-cols-12 base-responsive-gap">
                                        <div
                                            class="col-span-12 lg:col-span-8 2xl:col-span-6 2xl:col-start-2 flex flex-col justify-center items-start order-2 lg:order-1"
                                        >
                                            <h3 class="header-3 mb-2"><T :t="item.titleKey" /></h3>
                                            <div class="font-bold mb-6"><T :t="item.constructionPeriodKey" /></div>
                                            <p class="mb-4"><T :t="item.descriptionKey" /></p>
                                            <Button
                                                :to="$t(item.urlKey)"
                                                target="_blank"
                                                :label="$t('generic.learn_more')"
                                                @focus.native="handleFocus"
                                            />
                                        </div>
                                        <div class="col-span-12 lg:col-span-4 order-1 lg:order-2">
                                            <StaticImage
                                                :img-key="item.staticImageKey"
                                                max-size="md"
                                                wrapper-class="aspect-w-1 aspect-h-1 rounded-full overflow-hidden"
                                                image-class="w-full h-full object-cover"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div
                                    class="absolute z-2 -bottom-80 lg:bottom-0 left-0 w-[200vw] lg:w-full h-96 lg:h-40 bg-cover"
                                    :style="`background-image: url(${require('@/assets/images/shapes/waves/wave-repeat-pattern-gray-100.png')}); background-position: top center`"
                                ></div>
                                <div
                                    class="absolute z-1 bottom-8 left-0 w-full h-[calc(100vh-4rem)] bg-cover opacity-70 lg:opacity-100"
                                    :style="`background-image: url(${require('assets/images/shapes/gradients/rasterized-gradient-blue-400.png')}); background-position: top center;`"
                                ></div>

                                <div v-if="i % 3 === 0" class="absolute w-full lg:h-40 bottom-0 z-3">
                                    <div
                                        class="bg-illustration bottom-4 left-12 lg:left-32 w-28 h-20 lg:w-40 lg:h-28"
                                        style="transform: rotateY(180deg)"
                                        v-lazy-background :lazy-background="require('~/assets/images/illustrations/picnic-woman.svg')"
                                    ></div>
                                    <div
                                        class="bg-illustration bottom-12 right-24 w-20 h-24 lg:w-28 lg:h-40 hidden lg:block"
                                        v-lazy-background :lazy-background="require('~/assets/images/illustrations/man.svg')"
                                    ></div>
                                    <div
                                        class="bg-illustration bottom-12 left-[75%] lg:left-[25%] w-8 h-16"
                                        v-lazy-background :lazy-background="require('~/assets/images/illustrations/tree1.svg')"
                                    ></div>
                                    <div
                                        class="bg-illustration bottom-10 left-[50%] w-8 h-16"
                                        v-lazy-background :lazy-background="require('~/assets/images/illustrations/tree2.svg')"
                                    ></div>
                                </div>

                                <div v-else-if="i % 2 !== 0" class="absolute w-full lg:h-40 bottom-0 z-3">
                                    <div
                                        class="bg-illustration bottom-10 left-1/4 lg:left-6 w-44 h-20 lg:w-48 lg:h-28"
                                        v-lazy-background :lazy-background="require('~/assets/images/illustrations/yoga-couple.svg')"
                                    ></div>
                                    <div
                                        class="bg-illustration bottom-14 right-[10%] w-20 h-24 lg:w-28 lg:h-32 hidden lg:block"
                                        v-lazy-background :lazy-background="
                                            require('~/assets/images/illustrations/woman-with-basket.svg')
                                        "
                                    ></div>
                                    <div
                                        class="bg-illustration bottom-4 lg:bottom-24 right-4 lg:right-unset lg:left-[50%] w-8 h-16"
                                        v-lazy-background :lazy-background="require('~/assets/images/illustrations/tree3.svg')"
                                    ></div>
                                    <div
                                        class="bg-illustration bottom-4 left-[35%] w-32 h-32 hidden lg:block"
                                        v-lazy-background :lazy-background="require('~/assets/images/illustrations/flower-group-2.svg')"
                                    ></div>
                                </div>

                                <div v-else class="absolute w-full lg:h-40 bottom-0 z-3">
                                    <div
                                        class="bg-illustration bottom-8 left-12 lg:w-20 lg:h-20"
                                        style="transform: rotateY(180deg)"
                                        v-lazy-background :lazy-background="
                                            require('~/assets/images/illustrations/kid-throwing-football.svg')
                                        "
                                    ></div>
                                    <div
                                        class="bg-illustration bottom-12 left-[40%] w-14 h-14"
                                        v-lazy-background :lazy-background="require('~/assets/images/illustrations/roses.svg')"
                                    ></div>
                                    <div
                                        class="bg-illustration bottom-8 right-[calc(32%)] lg:w-36 lg:h-32"
                                        v-lazy-background :lazy-background="require('~/assets/images/illustrations/bicycle.svg')"
                                    ></div>
                                    <div
                                        class="bg-illustration bottom-10 left-8 lg:left-[20%] w-8 h-16"
                                        v-lazy-background :lazy-background="require('~/assets/images/illustrations/tree2.svg')"
                                    ></div>
                                    <div
                                        class="bg-illustration bottom-4 right-12 w-20 h-20"
                                        v-lazy-background :lazy-background="require('~/assets/images/illustrations/birds.svg')"
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <div class="w-full h-64 relative z-2" style="transform: rotateY(180deg)">
            <div
                class="absolute z-1 top-0 left-0 w-full h-[50vh] bg-cover"
                :style="`background-image: url(${require('@/assets/images/shapes/gradients/rasterized-gradient-gray-100.png')}); background-position: top center;`"
            ></div>
            <div class="absolute h-full z-2 bottom-0 left-0 w-full bg-gradient-to-t from-gray-100"></div>
        </div>
    </div>
</template>

<script>
import { gsap } from 'gsap/dist/gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';

export default {
    name: 'OurFirst10Years',
    data() {
        return {
            items: [
                {
                    titleKey: 'our_first_10_years.house_of_hungarian_music.title',
                    descriptionKey: 'our_first_10_years.house_of_hungarian_music.description',
                    constructionPeriodKey: 'our_first_10_years.house_of_hungarian_music.construction_period',
                    staticImageKey: 'our-first-10-years___house-of-the-hungarian-music',
                    urlKey: 'our_first_10_years.house_of_hungarian_music.url',
                },
                {
                    titleKey: 'our_first_10_years.museum_of_ethnography.title',
                    descriptionKey: 'our_first_10_years.museum_of_ethnography.description',
                    constructionPeriodKey: 'our_first_10_years.museum_of_ethnography.construction_period',
                    staticImageKey: 'our-first-10-years___museum-of-ethnography',
                    urlKey: 'our_first_10_years.museum_of_ethnography.url',
                },
                {
                    titleKey: 'our_first_10_years.museum_of_fine_arts.title',
                    descriptionKey: 'our_first_10_years.museum_of_fine_arts.description',
                    constructionPeriodKey: 'our_first_10_years.museum_of_fine_arts.construction_period',
                    staticImageKey: 'our-first-10-years___museum-of-fine-arts',
                    urlKey: 'our_first_10_years.museum_of_fine_arts.url',
                },
                {
                    titleKey: 'our_first_10_years.millennium_neo.title',
                    descriptionKey: 'our_first_10_years.millennium_neo.description',
                    constructionPeriodKey: 'our_first_10_years.millennium_neo.construction_period',
                    staticImageKey: 'our-first-10-years___millennium-neo',
                    urlKey: 'our_first_10_years.millennium_neo.url',
                },
                {
                    titleKey: 'our_first_10_years.omrrk.title',
                    descriptionKey: 'our_first_10_years.omrrk.description',
                    constructionPeriodKey: 'our_first_10_years.omrrk.construction_period',
                    staticImageKey: 'our-first-10-years___omrrk',
                    urlKey: 'our_first_10_years.omrrk.url',
                },
                {
                    titleKey: 'our_first_10_years.underground_parking.title',
                    descriptionKey: 'our_first_10_years.underground_parking.description',
                    constructionPeriodKey: 'our_first_10_years.underground_parking.construction_period',
                    staticImageKey: 'our-first-10-years___underground-parking',
                    urlKey: 'our_first_10_years.underground_parking.url',
                },
                {
                    titleKey: 'our_first_10_years.star_fortress_in_komarom.title',
                    descriptionKey: 'our_first_10_years.star_fortress_in_komarom.description',
                    constructionPeriodKey: 'our_first_10_years.star_fortress_in_komarom.construction_period',
                    staticImageKey: 'our-first-10-years___star-fortress-in-komarom',
                    urlKey: 'our_first_10_years.star_fortress_in_komarom.url',
                },
                {
                    titleKey: 'our_first_10_years.playground.title',
                    descriptionKey: 'our_first_10_years.playground.description',
                    constructionPeriodKey: 'our_first_10_years.playground.construction_period',
                    staticImageKey: 'our-first-10-years___playground',
                    urlKey: 'our_first_10_years.playground.url',
                },
                {
                    titleKey: 'our_first_10_years.sport_center.title',
                    descriptionKey: 'our_first_10_years.sport_center.description',
                    constructionPeriodKey: 'our_first_10_years.sport_center.construction_period',
                    staticImageKey: 'our-first-10-years___sport-center',
                    urlKey: 'our_first_10_years.sport_center.url',
                },
                {
                    titleKey: 'our_first_10_years.pavilon.title',
                    descriptionKey: 'our_first_10_years.pavilon.description',
                    constructionPeriodKey: 'our_first_10_years.pavilon.construction_period',
                    staticImageKey: 'our-first-10-years___pavilon',
                    urlKey: 'our_first_10_years.pavilon.url',
                },
                {
                    titleKey: 'our_first_10_years.park_for_dogs.title',
                    descriptionKey: 'our_first_10_years.park_for_dogs.description',
                    constructionPeriodKey: 'our_first_10_years.park_for_dogs.construction_period',
                    staticImageKey: 'our-first-10-years___park-for-dogs',
                    urlKey: 'our_first_10_years.park_for_dogs.url',
                },
                {
                    titleKey: 'our_first_10_years.mocsenyi_botanical_garden.title',
                    descriptionKey: 'our_first_10_years.mocsenyi_botanical_garden.description',
                    constructionPeriodKey: 'our_first_10_years.mocsenyi_botanical_garden.construction_period',
                    staticImageKey: 'our-first-10-years___mocsenyi-botanical-garden',
                    urlKey: 'our_first_10_years.mocsenyi_botanical_garden.url',
                },
                {
                    titleKey: 'our_first_10_years.promenad.title',
                    descriptionKey: 'our_first_10_years.promenad.description',
                    constructionPeriodKey: 'our_first_10_years.promenad.construction_period',
                    staticImageKey: 'our-first-10-years___promenad',
                    urlKey: 'our_first_10_years.promenad.url',
                },
                {
                    titleKey: 'our_first_10_years.sensory_garden.title',
                    descriptionKey: 'our_first_10_years.sensory_garden.description',
                    constructionPeriodKey: 'our_first_10_years.sensory_garden.construction_period',
                    staticImageKey: 'our-first-10-years___sensory-garden',
                    urlKey: 'our_first_10_years.sensory_garden.url',
                },
                {
                    titleKey: 'our_first_10_years.sport_facilities.title',
                    descriptionKey: 'our_first_10_years.sport_facilities.description',
                    constructionPeriodKey: 'our_first_10_years.sport_facilities.construction_period',
                    staticImageKey: 'our-first-10-years___sport-facilities',
                    urlKey: 'our_first_10_years.sport_facilities.url',
                },
            ],
        };
    },
    head() {
        return {
            title: this.$getPageTitle('our-first-10-years.index'),
            meta: this.$getPageMetaData('our-first-10-years.index'),
        };
    },
    mounted() {
        if (this.$device.isMobileOrTablet) {
            return;
        }

        window.scrollTo(0, 0);

        gsap.registerPlugin(ScrollTrigger);

        const listWrapperEl = document.querySelector('.side-scroll-list-wrapper');
        const listEl = document.querySelector('.side-scroll-list');

        gsap.to(listEl, {
            x: () => -(listEl.clientWidth - listWrapperEl.clientWidth),
            ease: 'none',
            scrollTrigger: {
                trigger: '.side-scroll',
                start: 'top top',
                end: () => `+=${listEl.clientWidth - listWrapperEl.clientWidth}`,
                scrub: true,
                pin: true,
                anticipatePin: 1,
                invalidateOnRefresh: true,
            },
        });
    },
    methods: {
        handleFocus() {
            // console.log('focusing');
        },
    },
};
</script>

<style scoped></style>
